"use client";

import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/icons";
import { useLogin } from "@/hooks/auth/useLogin";
import { useI18n } from "@/lib/i18n";
import { LoginModal, useLoginModal } from "@/components/LoginModal";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Loader2, ArrowRight, Building2, Sparkles, ImageIcon,
  Zap, Shield, Globe, ChevronRight, User,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSession } from "@opencut/auth/client";

export default function HomePage() {
  const { t, localePath } = useI18n();
  const { isGoogleLoading, isAnyLoading, handleGoogleLogin } = useLogin();
  const { open: loginOpen, showLogin, setLoginOpen } = useLoginModal();
  const session = useSession();
  const isLoggedIn = !!session?.data?.user;
  const [mounted, setMounted] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);

  // Plan pricing from API
  const [planData, setPlanData] = useState<Record<string, any> | null>(null);
  useEffect(() => {
    setMounted(true);
    fetch("/api/v2/plans")
      .then(r => r.json())
      .then(data => { if (data.success) setPlanData(data.plans); })
      .catch(() => {});
  }, []);

  const industries = [
    { name: t.industries.architecture.nameShort, nameEn: t.industries.architecture.nameEn, description: t.industries.architecture.description, icon: Building2, accent: "#2DD4BF", image: "/images/arch-cover.png" },
    { name: t.industries.spa.nameShort, nameEn: t.industries.spa.nameEn, description: t.industries.spa.description, icon: Sparkles, accent: "#F472B6", image: "/images/spa-cover.png" },
    { name: t.industries.photoEdit.nameShort, nameEn: t.industries.photoEdit.nameEn, description: t.industries.photoEdit.description, icon: ImageIcon, accent: "#818CF8", image: "/images/photo-cover.png" },
  ];

  const features = [
    { icon: Zap, ...t.home.features.fast },
    { icon: Shield, ...t.home.features.secure },
    { icon: Globe, ...t.home.features.multiPlatform },
  ];

  /** Format VND */
  const formatVnd = (amount: number) =>
    amount === 0 ? t.home.pricing.free : amount.toLocaleString("vi-VN") + "₫";

  /** Build plan cards from API or fallback */
  const pricingPlans = [
    { name: "Free", color: "#64748B", daily: planData?.free?.dailyImages ?? 10, concurrent: planData?.free?.maxConcurrent ?? 1, monthly: planData?.free?.monthlyEstimate ?? 300, priceVnd: 0, isFree: true, popular: false },
    { name: "Pro", color: "#06B6D4", daily: planData?.pro?.dailyImages ?? 100, concurrent: planData?.pro?.maxConcurrent ?? 5, monthly: planData?.pro?.monthlyEstimate ?? 3000, priceVnd: planData?.pro?.priceVnd ?? 199000, isFree: false, popular: true },
    { name: "Ultra", color: "#F59E0B", daily: planData?.ultra?.dailyImages ?? 500, concurrent: planData?.ultra?.maxConcurrent ?? 5, monthly: planData?.ultra?.monthlyEstimate ?? 15000, priceVnd: planData?.ultra?.priceVnd ?? 499000, isFree: false, popular: false },
  ];

  /** Navigate if logged in, otherwise show login modal */
  const requireAuth = (path: string) => {
    if (isLoggedIn) {
      window.location.href = localePath(path);
    } else {
      showLogin();
    }
  };

  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /** Bottom tab definitions */
  const bottomTabs = [
    { icon: Building2, label: t.home.tabs.services, action: scrollToServices, active: true },
    { icon: Zap, label: t.home.tabs.pricing, action: () => { const el = document.getElementById("pricing-section"); el?.scrollIntoView({ behavior: "smooth" }); }, active: false },
    { icon: ImageIcon, label: t.home.tabs.cases, action: () => requireAuth("/dashboard"), active: false },
    { icon: User, label: t.home.tabs.profile, action: () => requireAuth("/dashboard"), active: false },
  ];
  return ( 
  <div className="text-white">
    Live
  </div>
  )

  // return (
  //   <div className="min-h-screen overflow-x-hidden" style={{ background: "#0B1120", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
  //     <style jsx global>{`
  //       @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
  //     `}</style>

  //     {/* Login Modal */}
  //     <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />

  //     {/* ====== MOBILE (< lg) ====== */}
  //     <div className="lg:hidden flex flex-col min-h-screen">
  //       <nav className="flex items-center justify-between px-5 py-4">
  //         <div className="flex items-center gap-2.5">
  //           <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)" }}>
  //             <Sparkles className="w-4 h-4 text-white" />
  //           </div>
  //           <span className="text-lg font-bold" style={{ color: "#F1F5F9" }}>MindAI</span>
  //         </div>
  //         <div className="flex items-center gap-2">
  //           <LanguageSwitcher compact />
  //           {isLoggedIn ? (
  //             <Link href={localePath("/dashboard")}>
  //               <Button className="cursor-pointer rounded-xl px-4 py-2 text-sm font-semibold"
  //                 style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //                 Dashboard
  //               </Button>
  //             </Link>
  //           ) : (
  //             <Button onClick={showLogin}
  //               className="cursor-pointer rounded-xl px-4 py-2 text-sm font-semibold"
  //               style={{ background: "#1E293B", color: "#94A3B8" }}>
  //               {t.common.login}
  //             </Button>
  //           )}
  //         </div>
  //       </nav>

  //       <div className="flex-1 overflow-y-auto px-5 pb-24">
  //         <div className="text-center mt-4 mb-6" style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(16px)", transition: "all 0.6s ease 0.1s" }}>
  //           <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold tracking-wide" style={{ background: "rgba(6, 182, 212, 0.1)", color: "#22D3EE", border: "1px solid rgba(6, 182, 212, 0.2)" }}>
  //             <Sparkles className="w-3 h-3" /> {t.home.badge}
  //           </span>
  //         </div>

  //         <div className="text-center mb-6" style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(24px)", transition: "all 0.8s ease 0.2s" }}>
  //           <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-4" style={{ color: "#F1F5F9" }}>
  //             {t.home.heroTitle1}<br />
  //             <span style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
  //               {t.home.heroTitle2}
  //             </span>
  //           </h1>
  //           <p className="text-sm leading-relaxed mx-auto max-w-xs" style={{ color: "#64748B" }}>{t.home.heroDescription}</p>
  //         </div>

  //         <div className="flex justify-center mb-10" style={{ opacity: mounted ? 1 : 0, transition: "all 0.6s ease 0.4s" }}>
  //           {isLoggedIn ? (
  //             <Link href={localePath("/dashboard")}>
  //               <Button className="cursor-pointer rounded-xl px-8 py-3.5 text-base font-semibold transition-all duration-300 hover:shadow-lg"
  //                 style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //                 <span>{t.common.goToDashboard || "Dashboard"}</span>
  //                 <ArrowRight className="w-5 h-5 ml-2" />
  //               </Button>
  //             </Link>
  //           ) : (
  //             <Button onClick={showLogin}
  //               className="cursor-pointer rounded-xl px-8 py-3.5 text-base font-semibold transition-all duration-300 hover:shadow-lg"
  //               style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //               <GoogleIcon />
  //               <span className="ml-2">{t.common.getStarted}</span>
  //             </Button>
  //           )}
  //         </div>

  //         <div ref={servicesRef} className="flex items-center justify-between mb-5" style={{ opacity: mounted ? 1 : 0, transition: "all 0.5s ease 0.5s" }}>
  //           <h2 className="text-lg font-bold" style={{ color: "#F1F5F9" }}>{t.home.servicesByIndustry}</h2>
  //           <button className="flex items-center gap-1 text-xs font-semibold cursor-pointer" style={{ color: "#06B6D4" }}>
  //             {t.common.viewAll} <ArrowRight className="w-3 h-3" />
  //           </button>
  //         </div>

  //         <div className="space-y-4">
  //           {industries.map((ind, i) => (
  //             <div key={ind.nameEn} className="group cursor-pointer rounded-2xl overflow-hidden transition-all duration-300"
  //               style={{ background: "#1E293B", border: "1px solid rgba(255,255,255,0.06)", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s ease ${0.5 + i * 0.1}s` }}>
  //               <div className="relative h-44 sm:h-52 overflow-hidden">
  //                 <Image src={ind.image} alt={ind.nameEn} fill className="object-cover transition-transform duration-500 group-hover:scale-105" />
  //                 <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #1E293B 0%, transparent 60%)" }} />
  //               </div>
  //               <div className="px-5 pb-5 -mt-8 relative z-10">
  //                 <div className="flex items-center gap-3 mb-2">
  //                   <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ind.accent}20` }}>
  //                     <ind.icon className="w-4 h-4" style={{ color: ind.accent }} />
  //                   </div>
  //                 </div>
  //                 <h3 className="text-base font-bold mb-1" style={{ color: "#F1F5F9" }}>{ind.name}</h3>
  //                 <p className="text-xs" style={{ color: "#64748B" }}>{ind.description}</p>
  //               </div>
  //             </div>
  //           ))}
  //         </div>

  //         {/* Pricing Section */}
  //         <div id="pricing-section" className="mt-10 mb-6">
  //           <h2 className="text-xl font-bold mb-2" style={{ color: "#F1F5F9" }}>{t.home.pricing.title}</h2>
  //           <p className="text-xs mb-5" style={{ color: "#64748B" }}>{t.home.pricing.subtitle}</p>
  //           <div className="space-y-3">
  //             {pricingPlans.map((plan) => (
  //               <div key={plan.name} className="rounded-xl p-4 relative overflow-hidden"
  //                 style={{
  //                   background: "#1E293B",
  //                   border: plan.popular ? `2px solid ${plan.color}` : "1px solid rgba(255,255,255,0.06)",
  //                 }}>
  //                 {plan.popular && (
  //                   <span className="absolute top-3 right-3 text-[9px] font-bold uppercase px-2 py-0.5 rounded-full"
  //                     style={{ background: `${plan.color}20`, color: plan.color }}>
  //                     {t.home.pricing.popular}
  //                   </span>
  //                 )}
  //                 <div className="flex items-center gap-3 mb-1">
  //                   <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${plan.color}15` }}>
  //                     <Zap className="w-4 h-4" style={{ color: plan.color }} />
  //                   </div>
  //                   <div>
  //                     <h3 className="text-sm font-bold" style={{ color: plan.color }}>{plan.name}</h3>
  //                   </div>
  //                 </div>
  //                 <div className="mb-3 ml-11">
  //                   <span className="text-lg font-extrabold" style={{ color: "#F1F5F9" }}>
  //                     {formatVnd(plan.priceVnd)}
  //                   </span>
  //                   {!plan.isFree && <span className="text-[10px] ml-1" style={{ color: "#475569" }}>/tháng</span>}
  //                 </div>
  //                 <div className="grid grid-cols-3 gap-2 mb-3">
  //                   <div className="text-center rounded-lg py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                     <div className="text-base font-bold" style={{ color: "#F1F5F9" }}>{plan.daily}</div>
  //                     <div className="text-[9px]" style={{ color: "#475569" }}>{t.home.pricing.perDay}</div>
  //                   </div>
  //                   <div className="text-center rounded-lg py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                     <div className="text-base font-bold" style={{ color: "#F1F5F9" }}>{plan.concurrent}</div>
  //                     <div className="text-[9px]" style={{ color: "#475569" }}>{t.home.pricing.threads}</div>
  //                   </div>
  //                   <div className="text-center rounded-lg py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                     <div className="text-base font-bold" style={{ color: "#F1F5F9" }}>{plan.monthly.toLocaleString()}</div>
  //                     <div className="text-[9px]" style={{ color: "#475569" }}>{t.home.pricing.perMonth}</div>
  //                   </div>
  //                 </div>
  //                 <button
  //                   onClick={plan.isFree ? (isLoggedIn ? () => {} : showLogin) : () => requireAuth("/dashboard")}
  //                   className="w-full py-2.5 rounded-lg text-xs font-semibold transition-all cursor-pointer"
  //                   style={{
  //                     background: plan.popular ? plan.color : "rgba(255,255,255,0.05)",
  //                     color: plan.popular ? "#fff" : "#94A3B8",
  //                     border: plan.popular ? "none" : "1px solid rgba(255,255,255,0.08)",
  //                   }}>
  //                   {plan.isFree ? t.home.pricing.startFree : t.home.pricing.upgrade}
  //                 </button>
  //               </div>
  //             ))}
  //           </div>
  //         </div>
  //       </div>

  //       {/* Mobile Bottom Tabs — All clickable */}
  //       <div className="fixed bottom-0 left-0 right-0 z-50" style={{ background: "rgba(11, 17, 32, 0.95)", backdropFilter: "blur(16px)", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
  //         <div className="flex justify-around items-center py-3 px-4 max-w-md mx-auto">
  //           {bottomTabs.map((tab) => (
  //             <button key={tab.label} className="flex flex-col items-center gap-1 cursor-pointer transition-all active:scale-90" onClick={tab.action}>
  //               <tab.icon className="w-5 h-5" style={{ color: tab.active ? "#06B6D4" : "#475569" }} />
  //               <span className="text-[10px] font-semibold" style={{ color: tab.active ? "#06B6D4" : "#475569" }}>{tab.label}</span>
  //             </button>
  //           ))}
  //         </div>
  //       </div>
  //     </div>

  //     {/* ====== DESKTOP FIXED NAV ====== */}
  //     <div className="fixed top-0 left-0 right-0 z-50 hidden lg:block"
  //       style={{ background: "rgba(11, 17, 32, 0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
  //       <nav className="max-w-7xl mx-auto px-10 py-4 flex items-center justify-between" aria-label="Main navigation">
  //         <div className="flex items-center gap-3">
  //           <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)" }}>
  //             <Sparkles className="w-5 h-5 text-white" />
  //           </div>
  //           <span className="text-2xl font-bold" style={{ color: "#F1F5F9" }}>MindAI</span>
  //         </div>
  //         <div className="flex items-center gap-1">
  //           {[
  //             { label: t.home.tabs.services, target: "services-section" },
  //             { label: t.home.tabs.pricing, target: "pricing-section-desktop" },
  //           ].map((item) => (
  //             <button key={item.target}
  //               onClick={() => document.getElementById(item.target)?.scrollIntoView({ behavior: "smooth" })}
  //               className="px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer hover:bg-white/5"
  //               style={{ color: "#94A3B8" }}>
  //               {item.label}
  //             </button>
  //           ))}
  //           <Link href={localePath("/terms")} className="px-4 py-2 text-sm font-medium rounded-lg transition-colors hover:bg-white/5" style={{ color: "#94A3B8" }}>
  //             {t.common.termsOfService}
  //           </Link>
  //         </div>
  //         <div className="flex items-center gap-4">
  //           <LanguageSwitcher />
  //           {isLoggedIn ? (
  //             <Link href={localePath("/dashboard")}>
  //               <Button className="cursor-pointer rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
  //                 style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //                 Dashboard <ArrowRight className="w-4 h-4 ml-2" />
  //               </Button>
  //             </Link>
  //           ) : (
  //             <Button onClick={showLogin}
  //               className="cursor-pointer rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
  //               style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //               <GoogleIcon />
  //               <span className="ml-2">{t.common.getStarted}</span>
  //             </Button>
  //           )}
  //         </div>
  //       </nav>
  //     </div>

  //     {/* ====== DESKTOP (>= lg) ====== */}
  //     <div className="hidden lg:block pt-[72px]">
  //       <section className="max-w-7xl mx-auto px-10 pt-16 pb-20">
  //         <div className="grid grid-cols-2 gap-16 items-center">
  //           <div style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(32px)", transition: "all 1s cubic-bezier(0.16, 1, 0.3, 1)" }}>
  //             <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold tracking-wide mb-8" style={{ background: "rgba(6, 182, 212, 0.1)", color: "#22D3EE", border: "1px solid rgba(6, 182, 212, 0.2)" }}>
  //               <Sparkles className="w-3 h-3" /> {t.home.badge}
  //             </span>
  //             <h1 className="text-5xl xl:text-6xl font-extrabold leading-[1.1] tracking-tight mb-6" style={{ color: "#F1F5F9" }}>
  //               {t.home.heroTitle1}<br />
  //               <span style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t.home.heroTitle2}</span>
  //             </h1>
  //             <p className="text-lg leading-relaxed mb-10 max-w-md" style={{ color: "#64748B" }}>{t.home.heroDescription}</p>
  //             <div className="flex items-center gap-4">
  //               {isLoggedIn ? (
  //                 <Link href={localePath("/dashboard")}>
  //                   <Button className="cursor-pointer rounded-xl px-8 py-4 text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/20 hover:scale-[1.02] group"
  //                     style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //                     Dashboard
  //                     <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
  //                   </Button>
  //                 </Link>
  //               ) : (
  //                 <Button onClick={showLogin}
  //                   className="cursor-pointer rounded-xl px-8 py-4 text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/20 hover:scale-[1.02] group"
  //                   style={{ background: "linear-gradient(135deg, #06B6D4, #3B82F6)", color: "#fff" }}>
  //                   <GoogleIcon />
  //                   <span className="ml-2">{t.common.signInWithGoogle}</span>
  //                   <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
  //                 </Button>
  //               )}
  //             </div>
  //             <div className="flex items-center gap-8 mt-12">
  //               {[ { value: "12+", label: t.home.stats.aiTools }, { value: "3", label: t.home.stats.industriesCount }, { value: "<5s", label: t.home.stats.processing } ].map((s) => (
  //                 <div key={s.label}>
  //                   <div className="text-2xl font-extrabold" style={{ color: "#06B6D4" }}>{s.value}</div>
  //                   <div className="text-xs font-medium" style={{ color: "#475569" }}>{s.label}</div>
  //                 </div>
  //               ))}
  //             </div>
  //           </div>
  //           <div className="relative" style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(40px)", transition: "all 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s" }}>
  //             <div className="grid grid-cols-2 gap-4">
  //               <div className="rounded-2xl overflow-hidden h-64" style={{ background: "#1E293B" }}><Image src="/images/arch-cover.png" alt="Architecture" width={400} height={300} className="object-cover w-full h-full" /></div>
  //               <div className="rounded-2xl overflow-hidden h-64 mt-8" style={{ background: "#1E293B" }}><Image src="/images/spa-cover.png" alt="Spa" width={400} height={300} className="object-cover w-full h-full" /></div>
  //               <div className="col-span-2 rounded-2xl overflow-hidden h-48" style={{ background: "#1E293B" }}><Image src="/images/photo-cover.png" alt="Photo Editing" width={800} height={300} className="object-cover w-full h-full" /></div>
  //             </div>
  //           </div>
  //         </div>
  //       </section>

  //       {/* Services */}
  //       <section id="services-section" className="max-w-7xl mx-auto px-10 pb-24 scroll-mt-24">
  //         <div className="flex items-center justify-between mb-10">
  //           <h2 className="text-3xl font-extrabold" style={{ color: "#F1F5F9" }}>{t.home.servicesByIndustry}</h2>
  //           <button className="flex items-center gap-2 text-sm font-semibold cursor-pointer hover:opacity-80" style={{ color: "#06B6D4" }}>{t.common.viewAll} <ArrowRight className="w-4 h-4" /></button>
  //         </div>
  //         <div className="grid grid-cols-3 gap-6">
  //           {industries.map((ind, i) => (
  //             <div key={ind.nameEn} className="group cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
  //               style={{ background: "#1E293B", border: "1px solid rgba(255,255,255,0.06)", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(30px)", transition: `all 0.7s ease ${0.4 + i * 0.12}s` }}>
  //               <div className="relative h-56 xl:h-64 overflow-hidden">
  //                 <Image src={ind.image} alt={ind.nameEn} fill className="object-cover transition-transform duration-500 group-hover:scale-105" />
  //                 <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #1E293B 0%, transparent 50%)" }} />
  //               </div>
  //               <div className="px-6 pb-6 -mt-10 relative z-10">
  //                 <div className="flex items-center gap-3 mb-3">
  //                   <div className="w-10 h-10 rounded-xl flex items-center justify-center backdrop-blur-sm" style={{ background: `${ind.accent}15`, border: `1px solid ${ind.accent}30` }}>
  //                     <ind.icon className="w-5 h-5" style={{ color: ind.accent }} />
  //                   </div>
  //                 </div>
  //                 <h3 className="text-xl font-bold mb-1" style={{ color: "#F1F5F9" }}>{ind.name}</h3>
  //                 <p className="text-sm mb-4" style={{ color: "#64748B" }}>{ind.description}</p>
  //                 <div className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all duration-300" style={{ color: ind.accent }}>
  //                   <span>{t.common.explore}</span>
  //                   <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
  //                 </div>
  //               </div>
  //             </div>
  //           ))}
  //         </div>
  //       </section>

  //       {/* Pricing */}
  //       <section id="pricing-section-desktop" className="max-w-7xl mx-auto px-10 pb-24 scroll-mt-24">
  //         <div className="text-center mb-12">
  //           <h2 className="text-3xl font-extrabold mb-3" style={{ color: "#F1F5F9" }}>{t.home.pricing.title}</h2>
  //           <p className="text-base" style={{ color: "#64748B" }}>{t.home.pricing.subtitle}</p>
  //         </div>
  //         <div className="grid grid-cols-3 gap-6 max-w-5xl mx-auto">
  //           {pricingPlans.map((plan) => (
  //             <div key={plan.name} className="rounded-2xl p-6 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
  //               style={{
  //                 background: "#1E293B",
  //                 border: plan.popular ? `2px solid ${plan.color}` : "1px solid rgba(255,255,255,0.06)",
  //               }}>
  //               {plan.popular && (
  //                 <span className="absolute top-4 right-4 text-[10px] font-bold uppercase px-3 py-1 rounded-full"
  //                   style={{ background: `${plan.color}20`, color: plan.color }}>
  //                   {t.home.pricing.popular}
  //                 </span>
  //               )}
  //               <div className="flex items-center gap-3 mb-3">
  //                 <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${plan.color}15` }}>
  //                   <Zap className="w-6 h-6" style={{ color: plan.color }} />
  //                 </div>
  //                 <div>
  //                   <h3 className="text-xl font-bold" style={{ color: plan.color }}>{plan.name}</h3>
  //                 </div>
  //               </div>
  //               <div className="mb-6">
  //                 <span className="text-3xl font-extrabold" style={{ color: "#F1F5F9" }}>
  //                   {formatVnd(plan.priceVnd)}
  //                 </span>
  //                 {!plan.isFree && <span className="text-sm ml-1" style={{ color: "#475569" }}>/tháng</span>}
  //               </div>

  //               <div className="space-y-3 mb-6">
  //                 <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                   <span className="text-sm" style={{ color: "#94A3B8" }}>{t.home.pricing.features.dailyLimit}</span>
  //                   <span className="text-sm font-bold" style={{ color: "#F1F5F9" }}>{plan.daily} {t.home.pricing.perDay}</span>
  //                 </div>
  //                 <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                   <span className="text-sm" style={{ color: "#94A3B8" }}>{t.home.pricing.features.concurrent}</span>
  //                   <span className="text-sm font-bold" style={{ color: "#F1F5F9" }}>{plan.concurrent} {t.home.pricing.threads}</span>
  //                 </div>
  //                 <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                   <span className="text-sm" style={{ color: "#94A3B8" }}>{t.home.pricing.features.monthlyEst}</span>
  //                   <span className="text-sm font-bold" style={{ color: "#F1F5F9" }}>~{plan.monthly.toLocaleString()} {t.home.pricing.perMonth}</span>
  //                 </div>
  //                 <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                   <span className="text-sm" style={{ color: "#94A3B8" }}>{t.home.pricing.features.allTools}</span>
  //                   <span className="text-sm font-bold" style={{ color: "#22C55E" }}>✓</span>
  //                 </div>
  //                 <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
  //                   <span className="text-sm" style={{ color: "#94A3B8" }}>{t.home.pricing.features.support}</span>
  //                   <span className="text-sm font-bold" style={{ color: "#F1F5F9" }}>{plan.popular || plan.name === "Ultra" ? t.home.pricing.features.priority : t.home.pricing.features.basic}</span>
  //                 </div>
  //               </div>

  //               <button
  //                 onClick={plan.isFree ? (isLoggedIn ? () => {} : showLogin) : () => requireAuth("/dashboard")}
  //                 className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-300 hover:shadow-lg cursor-pointer"
  //                 style={{
  //                   background: plan.popular ? plan.color : "rgba(255,255,255,0.05)",
  //                   color: plan.popular ? "#fff" : "#94A3B8",
  //                   border: plan.popular ? "none" : "1px solid rgba(255,255,255,0.08)",
  //                 }}>
  //                 {plan.isFree ? t.home.pricing.startFree : t.home.pricing.upgrade}
  //               </button>
  //             </div>
  //           ))}
  //         </div>
  //       </section>

  //       <footer className="max-w-7xl mx-auto px-10 py-8" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
  //         <div className="flex justify-between items-center">
  //           <span className="text-sm" style={{ color: "#475569" }}>{t.common.copyright}</span>
  //           <Link href={localePath("/terms")} className="text-sm cursor-pointer hover:opacity-70 transition-opacity" style={{ color: "#475569" }}>{t.common.termsOfService}</Link>
  //         </div>
  //       </footer>
  //     </div>
  //   </div>
  // );
}
